import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { NativeFigureCurve, NativeFigureObject } from '../thermal-resistivity-native-soil/thermal-resistivity-native-soil.component';

@Component({
    selector: 'app-figure-ordering-popup',
    templateUrl: './figure-ordering-popup.component.html',
    styleUrls: ['./figure-ordering-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FigureOrderingPopupComponent implements OnInit {
  @Input() numberOfFigures = 0;
  @Input() figures: NativeFigureObject[] = [];
  @Input() projectID = '';
  @Output() save = new EventEmitter<{
    figures: NativeFigureObject[];
    reordered: boolean;
  }>();

  @Output() cancel = new EventEmitter<void>();

  private initialSignature = '';
  private initialFigures: NativeFigureObject[] = [];

  loading = true;

  legendHeaderModalOpen = false;
  activeLegendIndex: number | null = null;
  legendHeaderDraft = '';

  ngOnInit(): void {
    this.initialFigures = JSON.parse(JSON.stringify(this.figures));
    this.initialSignature = this.computeOrderingSignature();
  }

  addFigureAfter(index: number): void {
    this.figures.splice(index + 1, 0, {
      maxX: null,
      maxY: null,
      legendHeader: null,
      figureNumber: index + 1,
      curveShown: true,
      nativeFigureCurves: [],
    });
  }

  removeFigure(index: number): void {
    if (this.figures[index]?.nativeFigureCurves.length) return;
    this.figures.splice(index, 1);
  }

  onSave(): void {
    const figureOrdering: NativeFigureObject[] = [];
    let k = 0;
    this.figures.forEach((figure) => {
      if (figure.nativeFigureCurves.length > 0) {
        figureOrdering.push({
          maxX: null,
          maxY: null,
          figureNumber: k,
          legendHeader: figure.legendHeader,
          curveShown: figure.curveShown,
          nativeFigureCurves: figure.nativeFigureCurves,
        });
        k += 1;
      }
    });
    const reordered = this.computeOrderingSignature() !== this.initialSignature;
    this.save.emit({
      figures: figureOrdering,
      reordered,
    });
  }

  private computeOrderingSignature(): string {
    return this.figures
      .filter((figure) => figure.nativeFigureCurves.length > 0)
      .map((figure) => figure.nativeFigureCurves.map((curve) => curve.groupID).join(','))
      .join('|');
  }

  onCancel(): void {
    this.figures.splice(0, this.figures.length, ...JSON.parse(JSON.stringify(this.initialFigures)));
    this.cancel.emit();
  }

  openLegendHeaderModal(index: number): void {
    this.activeLegendIndex = index;
    this.legendHeaderDraft = this.figures[index]?.legendHeader ?? '';
    this.legendHeaderModalOpen = true;
  }

  saveLegendHeader(): void {
    if (this.activeLegendIndex !== null) {
      this.figures[this.activeLegendIndex].legendHeader = this.legendHeaderDraft;
    }
    this.closeLegendHeaderModal();
  }

  closeLegendHeaderModal(): void {
    this.legendHeaderModalOpen = false;
    this.activeLegendIndex = null;
    this.legendHeaderDraft = '';
  }

  drop(event: CdkDragDrop<NativeFigureCurve[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }
}
